/**
 * Pagina completa per la gestione degli utenti con ruoli e permessi
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Refresh,
  MoreVert,
  Lock,
  LockOpen,
  ContentCopy,
  Group,
  Security,
} from '@mui/icons-material';
import usersApiService, { User, Role } from '../services/usersApiService';
import UserManagementDialog from '../components/users/UserManagementDialog';
import RoleManagementDialog from '../components/users/RoleManagementDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const UsersPageNew: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Users state
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(0);
  const [userRowsPerPage, setUserRowsPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuUser, setMenuUser] = useState<User | null>(null);

  // Roles state
  const [roles, setRoles] = useState<Role[]>([]);
  const [filteredRoles, setFilteredRoles] = useState<Role[]>([]);
  const [roleSearch, setRoleSearch] = useState('');
  const [rolePage, setRolePage] = useState(0);
  const [roleRowsPerPage, setRoleRowsPerPage] = useState(10);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [roleMenuAnchor, setRoleMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRole, setMenuRole] = useState<Role | null>(null);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'user' | 'role'; id: string; name: string } | null>(null);

  useEffect(() => {
    loadData();
  }, [tabValue]);

  useEffect(() => {
    filterUsers();
  }, [users, userSearch]);

  useEffect(() => {
    filterRoles();
  }, [roles, roleSearch]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (tabValue === 0) {
        const usersData = await usersApiService.getUsers();
        setUsers(usersData);
      } else {
        const rolesData = await usersApiService.getRoles();
        setRoles(rolesData);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    if (!userSearch.trim()) {
      setFilteredUsers(users);
      return;
    }
    const search = userSearch.toLowerCase();
    setFilteredUsers(
      users.filter(
        (user) =>
          user.username.toLowerCase().includes(search) ||
          user.email.toLowerCase().includes(search) ||
          user.firstName?.toLowerCase().includes(search) ||
          user.lastName?.toLowerCase().includes(search) ||
          user.roleName?.toLowerCase().includes(search)
      )
    );
  };

  const filterRoles = () => {
    if (!roleSearch.trim()) {
      setFilteredRoles(roles);
      return;
    }
    const search = roleSearch.toLowerCase();
    setFilteredRoles(
      roles.filter(
        (role) => role.name.toLowerCase().includes(search) || role.description?.toLowerCase().includes(search)
      )
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setUserPage(0);
    setRolePage(0);
  };

  // User handlers
  const handleCreateUser = () => {
    setSelectedUser(null);
    setUserDialogOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setUserDialogOpen(true);
    setUserMenuAnchor(null);
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setLoading(true);
    try {
      await usersApiService.deleteUser(deleteTarget.id);
      await loadData();
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore nell\'eliminazione dell\'utente');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserStatus = async (user: User) => {
    setLoading(true);
    try {
      if (user.isActive) {
        await usersApiService.deleteUser(user.id);
      } else {
        await usersApiService.reactivateUser(user.id);
      }
      await loadData();
      setUserMenuAnchor(null);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore nel cambio stato utente');
    } finally {
      setLoading(false);
    }
  };

  // Role handlers
  const handleCreateRole = () => {
    setSelectedRole(null);
    setRoleDialogOpen(true);
  };

  const handleEditRole = (role: Role) => {
    setSelectedRole(role);
    setRoleDialogOpen(true);
    setRoleMenuAnchor(null);
  };

  const handleCloneRole = async (role: Role) => {
    setLoading(true);
    try {
      await usersApiService.cloneRole(role.id, `${role.name} (Copia)`);
      await loadData();
      setRoleMenuAnchor(null);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore nella clonazione del ruolo');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!deleteTarget) return;
    setLoading(true);
    try {
      await usersApiService.deleteRole(deleteTarget.id);
      await loadData();
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore nell\'eliminazione del ruolo');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (type: 'user' | 'role', id: string, name: string) => {
    setDeleteTarget({ type, id, name });
    setDeleteDialogOpen(true);
    setUserMenuAnchor(null);
    setRoleMenuAnchor(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Gestione Utenti e Ruoli
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={tabValue === 0 ? handleCreateUser : handleCreateRole}
          disabled={loading}
        >
          {tabValue === 0 ? 'Nuovo Utente' : 'Nuovo Ruolo'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Utenti" icon={<Group />} iconPosition="start" />
          <Tab label="Ruoli" icon={<Security />} iconPosition="start" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ px: 2, py: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Cerca utenti..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title="Ricarica">
                <IconButton onClick={loadData} disabled={loading}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Username</TableCell>
                        <TableCell>Nome Completo</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Ruolo</TableCell>
                        <TableCell>Stato</TableCell>
                        <TableCell>Ultimo Accesso</TableCell>
                        <TableCell align="right">Azioni</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredUsers
                        .slice(userPage * userRowsPerPage, userPage * userRowsPerPage + userRowsPerPage)
                        .map((user) => (
                          <TableRow key={user.id} hover>
                            <TableCell>
                              <strong>{user.username}</strong>
                            </TableCell>
                            <TableCell>
                              {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : '-'}
                            </TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                              <Chip label={user.roleName || user.role || 'Nessuno'} size="small" color="primary" />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={user.isActive ? 'Attivo' : 'Disattivo'}
                                size="small"
                                color={user.isActive ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('it-IT') : 'Mai'}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Modifica">
                                <IconButton size="small" onClick={() => handleEditUser(user)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Altro">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    setMenuUser(user);
                                    setUserMenuAnchor(e.currentTarget);
                                  }}
                                >
                                  <MoreVert fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={filteredUsers.length}
                  page={userPage}
                  onPageChange={(e, page) => setUserPage(page)}
                  rowsPerPage={userRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setUserRowsPerPage(parseInt(e.target.value, 10));
                    setUserPage(0);
                  }}
                  labelRowsPerPage="Righe per pagina:"
                />
              </>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: 2, py: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Cerca ruoli..."
                value={roleSearch}
                onChange={(e) => setRoleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title="Ricarica">
                <IconButton onClick={loadData} disabled={loading}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Nome Ruolo</TableCell>
                        <TableCell>Descrizione</TableCell>
                        <TableCell>Permessi</TableCell>
                        <TableCell>Menu</TableCell>
                        <TableCell>Stato</TableCell>
                        <TableCell align="right">Azioni</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRoles
                        .slice(rolePage * roleRowsPerPage, rolePage * roleRowsPerPage + roleRowsPerPage)
                        .map((role) => (
                          <TableRow key={role.id} hover>
                            <TableCell>
                              <strong>{role.name}</strong>
                            </TableCell>
                            <TableCell>{role.description || '-'}</TableCell>
                            <TableCell>
                              <Chip label={`${role.permissions?.length || 0} permessi`} size="small" />
                            </TableCell>
                            <TableCell>
                              <Chip label={`${role.menuPermissions?.length || 0} voci`} size="small" color="primary" />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={role.isActive ? 'Attivo' : 'Disattivo'}
                                size="small"
                                color={role.isActive ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Modifica">
                                <IconButton size="small" onClick={() => handleEditRole(role)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Altro">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    setMenuRole(role);
                                    setRoleMenuAnchor(e.currentTarget);
                                  }}
                                >
                                  <MoreVert fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={filteredRoles.length}
                  page={rolePage}
                  onPageChange={(e, page) => setRolePage(page)}
                  rowsPerPage={roleRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRoleRowsPerPage(parseInt(e.target.value, 10));
                    setRolePage(0);
                  }}
                  labelRowsPerPage="Righe per pagina:"
                />
              </>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* User Menu */}
      <Menu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={() => setUserMenuAnchor(null)}>
        {menuUser && (
          <>
            <MenuItem onClick={() => handleToggleUserStatus(menuUser)}>
              {menuUser.isActive ? <Lock fontSize="small" sx={{ mr: 1 }} /> : <LockOpen fontSize="small" sx={{ mr: 1 }} />}
              {menuUser.isActive ? 'Disattiva' : 'Riattiva'}
            </MenuItem>
            <MenuItem onClick={() => openDeleteDialog('user', menuUser.id, menuUser.username)}>
              <Delete fontSize="small" sx={{ mr: 1 }} color="error" />
              Elimina
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Role Menu */}
      <Menu anchorEl={roleMenuAnchor} open={Boolean(roleMenuAnchor)} onClose={() => setRoleMenuAnchor(null)}>
        {menuRole && (
          <>
            <MenuItem onClick={() => handleCloneRole(menuRole)}>
              <ContentCopy fontSize="small" sx={{ mr: 1 }} />
              Clona Ruolo
            </MenuItem>
            <MenuItem onClick={() => openDeleteDialog('role', menuRole.id, menuRole.name)}>
              <Delete fontSize="small" sx={{ mr: 1 }} color="error" />
              Elimina
            </MenuItem>
          </>
        )}
      </Menu>

      {/* User Dialog */}
      <UserManagementDialog
        open={userDialogOpen}
        user={selectedUser}
        onClose={() => {
          setUserDialogOpen(false);
          setSelectedUser(null);
        }}
        onSave={loadData}
      />

      {/* Role Dialog */}
      <RoleManagementDialog
        open={roleDialogOpen}
        role={selectedRole}
        onClose={() => {
          setRoleDialogOpen(false);
          setSelectedRole(null);
        }}
        onSave={loadData}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare {deleteTarget?.type === 'user' ? "l'utente" : 'il ruolo'} "
            {deleteTarget?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annulla</Button>
          <Button onClick={deleteTarget?.type === 'user' ? handleDeleteUser : handleDeleteRole} color="error" autoFocus>
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPageNew;
